import {
	ButtonBase,
	lighten,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useNotificationMarksAsReadMutation } from "web_myplann/Api/Notifications/useNotificationMarksAsReadMutation";
import { useGetDateString } from "web_myplann/Dashboard/Hooks/useGetDateString";
import {
	NotificationType,
	useGetDataByType,
} from "web_myplann/Dashboard/Hooks/useGetIconByType";
import {
	type NotificationListItemResponseDTO,
	NotificationTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import { device, NavigationTypes } from "web_myplann/Utils/device";

export const NotificationListItem: React.FC<
	NotificationListItemResponseDTO
> = ({
	type,
	title,
	content,
	createdAt,
	readAt,
	id,
	sourceId,
	subContent,
	customContent,
}) => {
	const { palette } = useTheme();
	const navigate = useNavigate();

	const { mutateAsync: markAsReadAsync } = useNotificationMarksAsReadMutation();

	const { icon, type: notificationType } = useGetDataByType(
		type,
		readAt ? palette.common.black : palette.primary.main,
	);
	const lightMain = lighten(
		palette.primary.main,
		palette.mode === "dark" ? 0.6 : 0.8,
	);
	const lightDesaturated = `color-mix(in oklch, ${lightMain} 85%, gray ${
		palette.mode === "dark" ? 50 : 5
	}%)`;
	const dateText = useGetDateString(createdAt);

	const isGroupedNotification =
		type === NotificationTypeEnum.ActivityGroup ||
		type === NotificationTypeEnum.TaskGroup;

	const markGroupedAsRead = async () => {
		if (Array.isArray(customContent?.notificationIds)) {
			await Promise.all(
				customContent.notificationIds.map((notificationId) =>
					markAsReadAsync(notificationId as number),
				),
			);
		}
	};

	const handleClick = async () => {
		if (isGroupedNotification) {
			await markGroupedAsRead();
		} else {
			await markAsReadAsync(id);
		}

		if (notificationType === NotificationType.meetingRecord) {
			if (customContent?.url && typeof customContent.url === "string") {
				device.navigationRequest(NavigationTypes.ZZJ, customContent?.url);
			}
			return;
		}
		if (notificationType === NotificationType.task) {
			navigate(Routing.TaskDetail.path(sourceId ?? ""));
		} else {
			navigate(Routing.ActivityDetail.path(sourceId ?? ""));
		}
	};

	return (
		<Stack
			direction="row"
			alignItems={subContent ? "flex-start" : "center"}
			gap={1.2}
			onClick={handleClick}
			component={ButtonBase}
		>
			<Stack
				height={40}
				width={40}
				alignItems="center"
				justifyContent="center"
				borderRadius={9999}
				bgcolor={readAt ? palette.deprecated.grey[100] : lightDesaturated}
				flexShrink={0}
			>
				{icon}
			</Stack>

			<Stack flex={1} sx={{ minWidth: 0 }}>
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="center"
				>
					<Typography
						variant="deprecatedBody2"
						color={palette.deprecated.grey[700]}
						lineHeight={"24px"}
					>
						{title}
					</Typography>
					<Typography
						variant={readAt ? "body1" : "body2"}
						color={readAt ? palette.deprecated.grey[700] : palette.primary.main}
					>
						{dateText}
					</Typography>
				</Stack>
				<Stack alignItems="flex-start" sx={{ width: "100%", minWidth: 0 }}>
					<Typography
						variant="deprecatedBody1"
						color={palette.deprecated.grey[500]}
						noWrap
						sx={{
							overflow: "hidden",
							textOverflow: "ellipsis",
							maxWidth: "100%",
						}}
						lineHeight={"24px"}
					>
						{content}
					</Typography>
				</Stack>
				<Stack alignItems="flex-start">
					<Typography
						variant="deprecatedBody1"
						color={palette.deprecated.grey[500]}
						lineHeight={"24px"}
					>
						{subContent}
					</Typography>
				</Stack>
			</Stack>
		</Stack>
	);
};
